import { useEffect, useState } from 'react';
import {
  registrarAvisoDeFallo, textoDelFallo, type FalloAnunciado,
} from '../lib/errores';

/* ════════════════════════════════════════════════════════════════════════════
   EL AVISO DE FALLO QUE NO SE PUEDE NO VER

   ── QUÉ ARREGLA (2026-09-03) ───────────────────────────────────────────────
   La usuaria: «varios usuarios realizan alguna acción —cargar una negociación,
   una planificación, una devolución— y luego no queda cargada y tampoco les
   sale ningún mensaje de error».

   Lo medido es que el mensaje casi nunca falta: se calcula bien, se traduce
   bien (`lib/errores.ts`) y se pinta FUERA DE LA PANTALLA. La distancia en
   líneas de JSX entre el botón que dispara y el sitio donde se pinta el fallo
   iba de 100 a 854 líneas en ocho pantallas, y en Devoluciones hay CUATRO
   secciones de formulario en medio: el asesor rellena el acta, baja hasta
   «Enviar solicitud» —la última línea de la página—, el rojo aparece 354
   líneas más arriba, el botón vuelve de «Generando ticket…» a «Enviar
   solicitud», y no pasa nada más.

   Este aviso va FIJO en el viewport, así que da igual dónde estuviera mirando.

   ── POR QUÉ FIJO Y ABAJO ───────────────────────────────────────────────────
   Porque el botón de guardar de un formulario largo está abajo y ahí está la
   vista de la persona cuando el fallo ocurre. Que tape el botón que acaba de
   pulsar es DELIBERADO: el segundo clic a ciegas sobre un guardado que quizá sí
   entró es justo lo que duplica el registro (gotcha 74). Para volver a pulsar
   hay que cerrar el aviso, o sea leerlo.

   ── POR QUÉ NO SE VA SOLO ──────────────────────────────────────────────────
   Un aviso de error con temporizador es un aviso que se pierde el que fue a
   buscar un dato al escritorio de al lado. Se queda hasta que lo cierran.

   ── LOS COLORES ────────────────────────────────────────────────────────────
   La regla de `componentes/Aviso.tsx`, sin excepción: sobre superficie teñida,
   tinta `-800` en claro y `-200` en oscuro. Aquí además el envoltorio es
   OPACO (`bg-panel`) y no teñido, que es distinto del resto de los avisos del
   portal: este flota sobre contenido cualquiera y con un fondo al 10 % se
   leería encima de una tabla. El candado `componentes/avisos.test.ts` vigila
   que la tinta no vuelva a los tonos claros sin su `dark:`.
   ════════════════════════════════════════════════════════════════════════════ */

export function AvisoDeFalloGlobal() {
  const [fallo, setFallo] = useState<FalloAnunciado | null>(null);

  useEffect(() => {
    registrarAvisoDeFallo(setFallo);
    return () => registrarAvisoDeFallo(null);
  }, []);

  if (!fallo) return null;
  const t = textoDelFallo(fallo);

  return (
    /* `pointer-events-none` en el carril y `auto` en la tarjeta: el carril
       ocupa el ancho de la pantalla para centrar, y sin esto se comería los
       clics de todo lo que hay debajo a los lados del aviso. */
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 sm:p-4">
      <div
        role="alert"
        className="pointer-events-auto w-full max-w-xl overflow-hidden rounded-2xl
                   border-2 border-rose-500 bg-panel shadow-2xl"
      >
        <div className="flex items-start gap-3 bg-rose-500/10 px-4 py-3
                        text-rose-800 dark:text-rose-200">
          <span aria-hidden className="mt-0.5 text-lg leading-none">⚠</span>

          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="text-sm font-bold leading-snug">{t.titulo}</p>

            {/* Lo que pasó, con las palabras de `lib/errores.ts`. */}
            <p className="text-xs leading-relaxed">{t.mensaje}</p>

            {/* Los campos que el servidor rechazó, cuando los dice. */}
            {t.campos && (
              <p className="text-xs leading-relaxed">
                <span className="font-semibold">Revise: </span>{t.campos}
              </p>
            )}

            {/* El renglón que faltaba: ¿lo repito o no? */}
            <p className="rounded-lg bg-rose-500/10 px-2 py-1.5 text-[11px] font-semibold leading-relaxed">
              {t.dato}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setFallo(null)}
            aria-label="Cerrar el aviso"
            className="-mr-1 -mt-1 rounded-lg px-2 py-1 text-lg font-bold leading-none
                       opacity-70 transition hover:opacity-100"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

export default AvisoDeFalloGlobal;
